import { useTranslation } from "react-i18next"

import { useApolloClient } from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import { evictProjectPrice } from "shared/api/price/evictPriceCache"
import { DeleteResponseTypename, MutationTypename } from "shared/api/types"
import { emitAppEvent } from "shared/lib/appEventEmitter"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { DELETE_PROJECT } from "shared/types/ProjectMutations"

import {
  DeleteProjectMutationVariables,
  useDeleteProjectMutation
} from "entities/projects/graphql/mutations.generated"
import { evictProjectHistory } from "entities/projects/graphql/project-history/evictProjectHistory"
import { useProjectsLazyQuery } from "entities/projects/graphql/queries.generated"
import { evictProjectAndSort } from "entities/projects/lib/evictProjectAndSort"

export function useDeleteProject() {
  const { t } = useTranslation()
  const { cache } = useApolloClient()
  const [mutate, rest] = useDeleteProjectMutation()
  const startProjectRequest = useProjectRequestMonitor()
  const [fetchProjects] = useProjectsLazyQuery()
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Project.DELETE_PROJECT.Start"),
      success: t("Request.Project.DELETE_PROJECT.Success"),
      failure: t("Request.Project.DELETE_PROJECT.Failure")
    }
  })

  const deleteProject = useEvent(
    (variables: DeleteProjectMutationVariables) => {
      evictProjectAndSort(variables.id, cache)

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      const completeProjectRequest = startProjectRequest({
        projectID: variables.id,
        requestType: DELETE_PROJECT
      })

      emitAppEvent(DELETE_PROJECT, { id: variables.id })

      return callMutation(mutate, {
        variables,
        transform: data => data.project.success,
        apolloOptions: {
          optimisticResponse: {
            __typename: MutationTypename,
            project: { __typename: DeleteResponseTypename, success: true }
          },
          onCompleted: () => {
            completeProjectRequest()
            evictProjectHistory(variables.id, cache)
            fetchProjects()
            raiseSuccessToast()
            evictProjectAndSort(variables.id, cache)
          },
          onError: () => {
            completeProjectRequest()
            evictProjectPrice(variables.id, cache, true)
            evictProjectHistory(variables.id, cache)
            fetchProjects()
            raiseErrorToast()
          }
        }
      })
    }
  )
  return {
    deleteProject,
    ...rest
  }
}
